<template>
  <v-container fluid class="login-wrapper fill-height d-flex align-center justify-center">
    <!-- Logo superior izquierdo -->
    <div class="hc-group__LOGO">
      <img
        :width="110"
        alt="Logo HC Group"
        :src="require('@/assets/Images/hcgroup.jpg')"
        class="brand-logo"
      />
    </div>

    <v-row justify="center" align="center" class="w-100">
      <v-col cols="12" sm="10" md="8" lg="5" xl="4">
        
        <!-- Header / Logotipos principales -->
        <div class="text-center mb-6">
          <img
            v-if="!isRAOS"
            :width="300"
            alt="Logo Smart Link"
            :src="require('@/assets/Images/logoSmartLink.png')"
            class="mb-2 img-fluid"
          />

          <template v-else>
            <h1 class="login-title">SmartLink™ <span class="highlight">RAOS</span></h1>
            <p class="login-subtitle">Remote & Autonomous Operation System</p>
          </template>
        </div>

        <!-- Tarjeta de Login -->
        <v-card class="login-card elevation-16 pa-6 pa-md-8">
          <v-card-title class="text-h5 text-center font-weight-bold mb-4 card-title-text">
            Iniciar Sesión
          </v-card-title>

          <!-- Formulario -->
          <v-form v-model="valid" @submit.prevent="submit">
            <v-text-field
              v-model="userName"
              :rules="fieldUserRules"
              :label="t('labels.user')"
              prepend-inner-icon="mdi-account-hard-hat"
              variant="outlined"
              density="comfortable"
              class="mb-3 custom-input"
              color="bgCircular"
              required
            ></v-text-field>

            <v-text-field
              v-model="password"
              :rules="fieldPassRules"
              :type="typePassword"
              :append-inner-icon="iconAppend"
              :label="t('labels.pass')"
              prepend-inner-icon="mdi-lock-outline"
              variant="outlined"
              density="comfortable"
              class="mb-2 custom-input"
              color="bgCircular"
              @click:append-inner="changeType"
              required
            ></v-text-field>

            <!-- Botones de Acción -->
            <v-row class="mt-4" dense>
              <v-col cols="12">
                <v-btn
                  type="submit"
                  color="btnSelect"
                  block
                  size="large"
                  class="btn-modern font-weight-bold text-white"
                  :disabled="!valid || loading"
                  :loading="loading"
                  elevation="4"
                >
                  {{ t('buttons.login') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card>

      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { Ref, computed, ref, onMounted } from 'vue';
import axios from 'axios';
import router from '@/router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useTheme } from "vuetify";

import { AUTH_LOGIN_URL, MINAS_CONFIG, changeURL, TIMEOUTTIME } from '@/constants/apis';
import { TOKEN, VISIBLE_PAGES } from '@/constants/labels';
import { showConsoleError } from '@/functions/errors';

const store = useStore();
const { t } = useI18n();
const theme = useTheme();

onMounted(() => {
  theme.change('darkCustom');
});

const projectName = computed(() => store.state.projectName);
const isRAOS = computed(() => projectName.value === 'SmartLinkRAOS');

const valid: Ref<boolean> = ref(false);
const loading: Ref<boolean> = ref(false);
const userName: Ref<string> = ref('');
const password: Ref<string> = ref('');
const iconAppend = ref('mdi-eye');
const typePassword = ref('password');
const boolPassword = ref(true);

const fieldPassRules = ref([
  (value: string) => !!value || t('labels.errors.passRequired')
]);

const fieldUserRules = computed(() => [
  (v: string) => !!v || t('labels.errors.userRequired'),
  (v: string) => (v && v.length >= 4) || t('labels.errors.userError'),
]);

const changeType = () => {
  boolPassword.value = !boolPassword.value;
  iconAppend.value = boolPassword.value ? 'mdi-eye' : 'mdi-eye-off';
  typePassword.value = boolPassword.value ? 'password' : 'text';
};

const submit = async () => {
  if (!valid.value) return;
  loading.value = true;

  try {
    const response = await axios.post(
      AUTH_LOGIN_URL,
      { 
        user: userName.value, 
        pass: password.value 
      },
      {
        timeout: TIMEOUTTIME,
        headers: { 
          "Authorization": `Bearer ${TOKEN}`,
          "Content-Type": "application/json"
        }
      }
    );

    const sesion = response.data.body;

    if (sesion.status === 'User Found') {
      localStorage.setItem('user', sesion.user);
      localStorage.setItem('rol', sesion.rol);
      store.commit('setUserSesion', sesion);
      // store.commit('setUserRol', sesion.rol);
      // store.commit('')

      // CASO 1: Desarrollador -> Redireccionar al Selector de Minas
      if (sesion.rol === 'desarrollador') {
        router.push('/principalPage');
        store.commit('setAppBarPage', 'principalPage');
      } 
      // CASO 2: Trabajador de Mina -> Redireccionar directo a su Dashboard
      else if (sesion.rol === 'trabajador_mina' && sesion.mina) {
        const keyMina = sesion.mina as keyof typeof MINAS_CONFIG;
        const configMina = MINAS_CONFIG[keyMina];

        if (configMina) {
          changeURL(configMina.index);
          store.commit('setProjectName', configMina.name);
          store.commit('setProjectLAT', configMina.lat);
          store.commit('setProjectLNG', configMina.lng);
          store.commit('setProjectZOOM', configMina.zoom);

          const finalURLTarget = isRAOS.value
            ? `/smartlinkRAOS/${VISIBLE_PAGES.raosinit}`
            // : `/smartlink/${VISIBLE_PAGES.dashboard}`;
            : configMina.index === 3 ? `/smartlink/cemex/${VISIBLE_PAGES.tepeaca.dashboard}`
            : `/smartlink/${VISIBLE_PAGES.dashboard}`;

          router.push(finalURLTarget);
          store.commit('setAppBarPage', 'smartlinkPage');
        }
      }
    }
  } catch (error: any) {
    let msg = t('snackbar.login.passWrong');
    if (error.response && error.response.status === 404) {
      msg = t('snackbar.login.userNotFound');
    }
    userName.value = '';
    password.value = '';
    store.dispatch('showSnackBar', { type: 'setSnackError', msg });
    showConsoleError(error);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.login-wrapper {
  position: relative;
  background-color: #1E1E2F;
  min-height: 100vh;
}
.hc-group__LOGO {
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 10;
}
.brand-logo {
  border-radius: 8px;
  filter: drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.5));
}
.login-title {
  font-weight: 800;
  font-size: 2.5rem;
  color: #FFFFFF;
}
.login-title .highlight {
  color: #C3EEFA;
}
.login-subtitle {
  color: #C2C2C2;
}
.login-card {
  background-color: #27293D !important;
  border: 1px solid #0E2F5A;
  border-radius: 20px !important;
}
.card-title-text {
  color: #FFFFFF;
}
.btn-modern {
  border-radius: 10px;
  text-transform: none;
}
</style>